import { ClipAudioEngine, clipAudioEngine, PreloadResult } from './clipAudioEngine'
import { reportClipMiss } from './clipMisses'
import { ClipEntry, clipsConfigured, clipsTooSlow, markClipsTooSlow, getClip, isClipPayload, registerClipTexts, resetClipRegistry } from './clipRegistry'
import { SpeechRequest } from './clipSource'
import { ClipPayload, ENGINE_KIND, ENGINE_REASON, EngineKind, EngineReason, FetchWatcher, SpeakOptions, SpeechEngine } from './speechEngine'
import { webSpeechEngine } from './webSpeechEngine'

export const START_TIMEOUT_MS = 1500
export const PRELOAD_TIMEOUT_MS = 4000
export const MIN_JUDGED_AUDIO_MS = 2000

export const SPEECH_EVENT = {
  DOWNLOADED: 'downloaded',
  FETCHED: 'fetched',
  SELECTED: 'selected',
  STALLED: 'stalled',
  STARTED: 'started',
} as const

export type SpeechEventName = typeof SPEECH_EVENT[keyof typeof SPEECH_EVENT]

export interface SpeechEvent {
  audioMs?: number;
  bytes?: number;
  downloadMs?: number;
  engine: EngineKind;
  event: SpeechEventName;
  latencyMs?: number;
  reason?: EngineReason;
  stallRisk?: number;
}

export type SpeechEventListener = (event: SpeechEvent) => void

let fallbackEngine: SpeechEngine = webSpeechEngine
let clipEngine: ClipAudioEngine = clipAudioEngine
let currentEngine: SpeechEngine | null = null
let currentToken: object | null = null
let currentAttempt: object | null = null
let currentInterrupt: (() => void) | null = null
let session = new AbortController()
const listeners = new Set<SpeechEventListener>()

const engineInUse = () => currentEngine || fallbackEngine

const release = () => {
  currentToken = null
  currentEngine = null
  currentAttempt = null
  currentInterrupt = null
}

const emit = (event: SpeechEvent) => listeners.forEach(listener => listener(event))

const report = (options: SpeakOptions, engine: EngineKind, reason: EngineReason) => {
  if (options.onEngine) { options.onEngine(engine, reason) }
  emit({ engine, event: SPEECH_EVENT.SELECTED, reason, })
}

export const subscribeToSpeechEvents = (listener: SpeechEventListener) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export const isFallbackPreferred = () => clipsTooSlow()

export const resetFallbackPreference = () => {
  resetClipRegistry()
  session = new AbortController()
}

const commitFallback = () => {
  if (clipsTooSlow()) { return false }

  markClipsTooSlow()
  session.abort()
  clipEngine.unload()
  return true
}

const preferFallback = (reason: EngineReason) => {
  if (commitFallback()) { emit({ engine: fallbackEngine.kind, event: SPEECH_EVENT.SELECTED, reason, }) }
}

const watchFetch: FetchWatcher = (url) => {
  if (session.signal.aborted) { return Promise.reject(session.signal.reason) }

  const requestedAt = Date.now()
  const timer = setTimeout(() => preferFallback(ENGINE_REASON.SLOW), PRELOAD_TIMEOUT_MS)
  const fetching = fetch(url, { signal: session.signal, })
  const settle = () => {
    clearTimeout(timer)
    if (!session.signal.aborted) { emit({ engine: ENGINE_KIND.CLIP, event: SPEECH_EVENT.FETCHED, latencyMs: Date.now() - requestedAt, }) }
  }
  fetching.then(settle, settle)
  return fetching
}

const clipDurationMs = (clip: ClipPayload) => (clip.words.length ? clip.words[clip.words.length - 1][1] : 0)

const downloadDeadline = (clip: ClipPayload) => {
  const audioMs = clipDurationMs(clip)
  return audioMs >= MIN_JUDGED_AUDIO_MS ? audioMs : undefined
}

const reportDownload = (clip: ClipPayload, result: PreloadResult | null) => {
  if (!result) { return }
  if (result.bytes === null) {
    preferFallback(ENGINE_REASON.THROUGHPUT)
    return
  }

  const audioMs = clipDurationMs(clip)
  if (audioMs <= 0) { return }

  const stallRisk = Math.round((result.downloadMs / audioMs) * 100) / 100
  emit({ audioMs, bytes: result.bytes, downloadMs: result.downloadMs, engine: ENGINE_KIND.CLIP, event: SPEECH_EVENT.DOWNLOADED, stallRisk, })
}

const notifyInterrupted = (options: SpeakOptions) => {
  if (options.onInterrupted) {
    options.onInterrupted()
  } else if (options.onEnd) {
    options.onEnd()
  }
}

export function setSpeechEngine(engine: SpeechEngine) {
  if (engine === fallbackEngine) { return }

  cancelSpeech()
  fallbackEngine = engine
}

export function setClipEngine(engine: ClipAudioEngine) {
  if (engine === clipEngine) { return }

  cancelSpeech()
  clipEngine = engine
}

export const activeSpeechEngineKind = (): EngineKind => engineInUse().kind

export const isTextToSpeechSupported = () => fallbackEngine.isSupported() || (clipsConfigured() && clipEngine.isSupported())

const fallbackReason = (entry: ClipEntry | undefined): EngineReason => (isClipPayload(entry) ? ENGINE_REASON.ERROR : (entry ?? ENGINE_REASON.MISSING))

function attempt(engine: SpeechEngine, token: object, text: string, options: SpeakOptions, startCharIndex: number, clip?: ClipPayload, requestedAt = Date.now()): object | null {
  const identity = {}
  const previousAttempt = currentAttempt
  currentAttempt = identity
  const live = () => currentAttempt === identity
  let lastCharIndex = startCharIndex
  let startTimer: ReturnType<typeof setTimeout> | null = null

  const clearStartTimer = () => {
    if (startTimer === null) { return }

    clearTimeout(startTimer)
    startTimer = null
  }

  const handOver = (reason: EngineReason) => {
    clearStartTimer()
    if (!fallbackEngine.isSupported() || !attempt(fallbackEngine, token, text, options, lastCharIndex, undefined, requestedAt)) { return false }

    report(options, fallbackEngine.kind, reason)
    return true
  }

  const handle = engine.speak(text, {
    ...options,
    clip,
    onStart: () => {
      if (!live()) { return }

      clearStartTimer()
      emit({ engine: engine.kind, event: SPEECH_EVENT.STARTED, latencyMs: Date.now() - requestedAt, })
      if (options.onStart) { options.onStart() }
    },
    onBoundary: (charIndex) => {
      if (!live()) { return }

      lastCharIndex = charIndex
      if (options.onBoundary) { options.onBoundary(charIndex) }
    },
    onEnd: () => {
      if (!live()) { return }

      if (currentToken === token) { release() }
      if (options.onEnd) { options.onEnd() }
    },
    onError: (reason) => {
      if (!live()) { return }

      if (engine !== fallbackEngine && handOver(ENGINE_REASON.ERROR)) { return }
      if (options.onError) { options.onError(reason) }
    },
    onStall: () => {
      if (!live()) { return }

      emit({ engine: engine.kind, event: SPEECH_EVENT.STALLED, })
      if (options.onStall) { options.onStall() }
    },
    startCharIndex,
  })
  if (!handle) {
    currentAttempt = previousAttempt
    return null
  }

  currentEngine = engine
  currentInterrupt = () => notifyInterrupted(options)
  if (engine !== fallbackEngine) {
    startTimer = setTimeout(() => {
      startTimer = null
      if (!live()) { return }

      engine.cancel()
      if (handOver(ENGINE_REASON.TIMEOUT)) {
        commitFallback()
        return
      }
      if (currentToken === token) { release() }
      if (options.onError) { options.onError(ENGINE_REASON.TIMEOUT) }
      if (options.onEnd) { options.onEnd() }
    }, START_TIMEOUT_MS)
  }
  return handle
}

export function speak(text: string, options: SpeakOptions = {}): object | null {
  if (!text || !text.trim()) { return null }

  const entry = getClip(text)
  const clip = isClipPayload(entry) ? entry : undefined
  const startCharIndex = options.startCharIndex ?? 0
  cancelSpeech()
  const token = {}
  currentToken = token

  if (clip && clipEngine.isSupported() && attempt(clipEngine, token, text, options, startCharIndex, clip)) {
    report(options, ENGINE_KIND.CLIP, ENGINE_REASON.READY)
    return token
  }

  if (!fallbackEngine.isSupported() || !attempt(fallbackEngine, token, text, options, startCharIndex)) {
    release()
    return null
  }
  report(options, fallbackEngine.kind, fallbackReason(entry))
  return token
}

export function cancelSpeech() {
  const engine = engineInUse()
  const notify = currentInterrupt
  release()
  engine.cancel()
  if (notify) { notify() }
}

export const isSpeakingUtterance = (handle: object | null) => !!handle && currentToken === handle

export const isSpeaking = () => currentToken !== null

export const pauseSpeech = () => engineInUse().pause()

export const resumeSpeech = () => engineInUse().resume()

export const seekSpeechToChar = (charIndex: number) => engineInUse().seekToChar(charIndex)

export const setSpeechRate = (rate: number) => engineInUse().setRate(rate)

export const prepareSpeech = (requests: SpeechRequest[]) => registerClipTexts(requests, { onMiss: reportClipMiss, watch: watchFetch, })

export function warmSpeech(text: string): Promise<void> {
  const entry = getClip(text)
  if (!isClipPayload(entry)) { return Promise.resolve() }

  return clipEngine.preload(entry.url, { deadlineMs: downloadDeadline(entry), watch: watchFetch, }).then(result => reportDownload(entry, result))
}
